import React, {
    MutableRefObject,
    useCallback,
    useRef,
} from 'react';
import {TableBody} from '@material-ui/core';
import AutoSizer from 'react-virtualized-auto-sizer';
import {FixedSizeList as List} from 'react-window';
import 'memoize-one';
import {curry} from 'ramda';
import Row from './Row';
import {RowData} from './tableTypes';

export interface BodyProps {
    itemCount: number;
    itemData: RowData;
}

export const TableList = curry(({itemCount, itemData, setListRef}, {height, width}) => {
    return (
        <List
            className="list"
            height={height}
            width={width}
            itemCount={itemCount}
            itemSize={itemData.rowHeight}
            itemKey={itemData.itemKey}
            itemData={itemData}
            ref={setListRef}
        >
            {Row}
        </List>
    );
});

export type ListRef = MutableRefObject<List | null>;

export type ListChangedProps = {
    listRef: ListRef;
    selectedRowIndex: number;
};

export const handleListChanged = curry((props: ListChangedProps, node: List) => {
    const {
        listRef,
        selectedRowIndex,
    } = props;
    
    listRef.current = node;
    
    if (listRef.current === null || selectedRowIndex === -1)
        return;
    
    listRef.current.scrollToItem(selectedRowIndex, 'auto');
});

const useListRef = (props: ListChangedProps) => {
    const {selectedRowIndex} = props;
    const onListChanged = handleListChanged(props);
    const setRef = useCallback(onListChanged, [selectedRowIndex]);
    
    return [setRef];
};

const Body = ({itemCount, itemData}: BodyProps) => {
    const listRef = useRef<List>(null);
    const {selectedRowIndex} = itemData;
    
    const [setListRef] = useListRef({
        listRef,
        selectedRowIndex,
    });
    
    const listRenderer = TableList({
        itemCount,
        itemData,
        setListRef,
    });
    
    return (
        <TableBody
            component="div"
            className="tbody"
        >
            <AutoSizer>
                {listRenderer}
            </AutoSizer>
        </TableBody>
    );
};

export default Body;
